import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useCurrencyRail } from "@/context/CurrencyRailContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Package, Percent, GitCompareArrows, TrendingUp, CheckSquare, Square, CheckCircle2, BookOpenCheck, Undo2, Download, FileText, AlertTriangle, Copy, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine,
} from "recharts";

const SYM = { USD: "$", NGN: "₦" };
function fm(n, ccy = "USD") {
  // Accountant-friendly signed currency: -$45.00 (not $-45.00).
  const v = Number(n || 0);
  const sym = SYM[ccy] || "$";
  return v < 0 ? `-${sym}${Math.abs(v).toFixed(2)}` : `${sym}${v.toFixed(2)}`;
}

export default function Financials() {
  const location = useLocation();
  // Deep-link support: /financials?tab=reconciliation opens the Recon tab
  // directly (used by the sidebar "Reconciliation" shortcut).
  const initialTab = (() => {
    const q = new URLSearchParams(location.search).get("tab");
    if (q === "reconciliation" || q === "recon") return "recon";
    if (q === "tax") return "tax";
    if (q === "forecast") return "forecast";
    return "assets";
  })();
  const [tab, setTab] = useState(initialTab);
  useEffect(() => { setTab(initialTab); }, [initialTab]);
  return (
    <AppShell>
      <div className="mb-8">
        <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Operations</div>
        <h1 className="font-display text-4xl mt-2">Financial operations</h1>
        <p className="text-sm text-[var(--muted-text)] mt-1 max-w-xl">
          Fixed assets, tax rates, bank reconciliation, and the 13-week cash forecast — everything to keep the books tight.
        </p>
      </div>
      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <TabsList className="bg-[var(--hover)] flex-wrap h-auto">
          <TabsTrigger value="assets" data-testid="tab-assets"><Package className="w-4 h-4 mr-2" /> Fixed Assets</TabsTrigger>
          <TabsTrigger value="tax" data-testid="tab-tax"><Percent className="w-4 h-4 mr-2" /> Sales Tax</TabsTrigger>
          <TabsTrigger value="recon" data-testid="tab-recon"><GitCompareArrows className="w-4 h-4 mr-2" /> Reconciliation</TabsTrigger>
          <TabsTrigger value="forecast" data-testid="tab-forecast"><TrendingUp className="w-4 h-4 mr-2" /> Cash Forecast</TabsTrigger>
        </TabsList>
        <TabsContent value="assets"><FixedAssetsTab /></TabsContent>
        <TabsContent value="tax"><TaxTab /></TabsContent>
        <TabsContent value="recon"><ReconciliationTab /></TabsContent>
        <TabsContent value="forecast"><ForecastTab /></TabsContent>
      </Tabs>
    </AppShell>
  );
}

// ----------- Fixed Assets -----------
function FixedAssetsTab() {
  const [assets, setAssets] = useState([]);
  const [register, setRegister] = useState(null);
  const [open, setOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  function blank() {
    return {
      name: "", cost: "", purchase_date: format(new Date(), "yyyy-MM-dd"),
      useful_life_years: 5, salvage_value: 0, method: "straight_line", notes: "",
    };
  }
  const load = async () => {
    const [{ data: a }, { data: r }] = await Promise.all([
      api.get("/fixed-assets"),
      api.get("/reports/fixed-assets-register"),
    ]);
    setAssets(a); setRegister(r);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim() || !form.cost) return toast.error("Name and cost required");
    const payload = {
      name: form.name.trim(),
      cost: parseFloat(form.cost),
      purchase_date: form.purchase_date,
      useful_life_years: parseInt(form.useful_life_years),
      salvage_value: parseFloat(form.salvage_value || 0),
      method: form.method,
      notes: form.notes,
    };
    try {
      if (editing) await api.patch(`/fixed-assets/${editing.id}`, payload);
      else await api.post("/fixed-assets", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this asset?")) return;
    try { await api.delete(`/fixed-assets/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error("Delete failed"); }
  };

  const openSchedule = async (a) => {
    try {
      const { data } = await api.get(`/fixed-assets/${a.id}/schedule`);
      setSchedule(data); setScheduleOpen(a);
    } catch (err) { toast.error("Failed to load schedule"); }
  };

  return (
    <div className="space-y-4">
      {register && (
        <div className="grid grid-cols-3 gap-4">
          <div className="card-flat p-4"><div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Total cost</div><div className="font-mono-data text-lg mt-1" data-testid="fa-total-cost">{fm(register.totals.cost)}</div></div>
          <div className="card-flat p-4"><div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Accumulated depr.</div><div className="font-mono-data text-lg mt-1 text-[#B4283A]" data-testid="fa-total-accum">{fm(register.totals.accumulated)}</div></div>
          <div className="card-flat p-4 border-l-4 border-l-[#E07A5F]"><div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Net book value</div><div className="font-mono-data text-lg mt-1" data-testid="fa-total-nbv">{fm(register.totals.book_value)}</div></div>
        </div>
      )}
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="fa-new-btn" onClick={() => { setEditing(null); setForm(blank()); setOpen(true); }} className="rounded-full bg-[#E07A5F] hover:bg-[#c9694f] text-white h-10 px-4">
              <Plus className="w-4 h-4 mr-2" /> New asset
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editing ? "Edit asset" : "New fixed asset"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Name</Label><Input data-testid="fa-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Cost</Label><Input type="number" step="0.01" data-testid="fa-form-cost" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} /></div>
              <div><Label>Salvage value</Label><Input type="number" step="0.01" value={form.salvage_value} onChange={(e) => setForm({ ...form, salvage_value: e.target.value })} /></div>
              <div><Label>Purchase date</Label><Input type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} /></div>
              <div><Label>Useful life (years)</Label><Input type="number" value={form.useful_life_years} onChange={(e) => setForm({ ...form, useful_life_years: e.target.value })} /></div>
              <div className="col-span-2"><Label>Method</Label>
                <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                  <SelectTrigger data-testid="fa-form-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="straight_line">Straight-line</SelectItem>
                    <SelectItem value="declining_balance">Declining balance (2x)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="fa-form-save" onClick={save} className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="card-flat overflow-hidden">
        {assets.length === 0 ? (
          <div className="p-10 text-center text-sm text-[var(--muted-text)]">No fixed assets yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[var(--hover)] text-xs uppercase tracking-wider text-[var(--muted-text)]">
              <tr><th className="text-left px-5 py-2.5">Asset</th><th className="text-left px-4 py-2.5">Purchased</th><th className="text-left px-4 py-2.5">Method</th><th className="text-right px-4 py-2.5">Cost</th><th className="text-right px-4 py-2.5">Book value</th><th className="w-40" /></tr>
            </thead>
            <tbody>
              {assets.map((a) => (
                <tr key={a.id} data-testid={`fa-row-${a.id}`} className="border-b border-[var(--divider)] last:border-none">
                  <td className="px-5 py-3 font-medium">{a.name}</td>
                  <td className="px-4 py-3 text-[var(--muted-text)]">{a.purchase_date} · {a.useful_life_years}yr</td>
                  <td className="px-4 py-3 text-[var(--muted-text)]">{a.method === "declining_balance" ? "DB 2x" : "SL"}</td>
                  <td className="px-4 py-3 text-right font-mono">{fm(a.cost)}</td>
                  <td className="px-4 py-3 text-right font-mono">{fm(a.book_value)}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => openSchedule(a)} data-testid={`fa-schedule-${a.id}`}>Schedule</Button>
                    <Button size="icon" variant="ghost" data-testid={`fa-post-${a.id}`} title="Post current-year depreciation" onClick={async () => {
                      try {
                        const { data } = await api.post(`/fixed-assets/${a.id}/post-depreciation`);
                        toast.success(data.already_posted ? "Already posted for this year" : `Posted ${fm(data.amount)} depreciation`);
                        load();
                      } catch (err) { toast.error(err.response?.data?.detail || "Post failed"); }
                    }}>
                      <BookOpenCheck className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => { setEditing(a); setForm({ name: a.name, cost: a.cost, purchase_date: a.purchase_date, useful_life_years: a.useful_life_years, salvage_value: a.salvage_value, method: a.method, notes: a.notes || "" }); setOpen(true); }}><Pencil className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(a.id)}><Trash2 className="w-4 h-4 text-[#E63946]" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!scheduleOpen} onOpenChange={(v) => !v && setScheduleOpen(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{scheduleOpen?.name} · schedule</DialogTitle><DialogDescription>Method: {scheduleOpen?.method}. Reversing a year creates an offsetting journal entry — the original stays in the ledger for audit.</DialogDescription></DialogHeader>
          {schedule && (
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-wider text-[var(--muted-text)] border-b border-[var(--divider)]"><th className="text-left py-2">Year</th><th className="text-right py-2">Depreciation</th><th className="text-right py-2">Accumulated</th><th className="text-right py-2">Book value</th><th className="w-40 text-right py-2">Ledger</th></tr></thead>
              <tbody>
                {schedule.schedule.map((r) => {
                  const isPosted = (scheduleOpen?.posted_depreciation_years || []).includes(r.year);
                  return (
                    <tr key={r.year} className="border-b border-[var(--divider)] last:border-none">
                      <td className="py-2">{r.year}</td>
                      <td className="py-2 text-right font-mono">{fm(r.depreciation)}</td>
                      <td className="py-2 text-right font-mono text-[#B4283A]">{fm(r.accumulated)}</td>
                      <td className="py-2 text-right font-mono">{fm(r.book_value)}</td>
                      <td className="py-2 text-right">
                        {isPosted ? (
                          <Button size="sm" variant="ghost" data-testid={`fa-reverse-${scheduleOpen.id}-${r.year}`} title={`Reverse ${r.year} depreciation`} onClick={async () => {
                            if (!window.confirm(`Reverse ${r.year} depreciation for ${scheduleOpen.name}? An offsetting journal entry will be posted; the original remains for audit.`)) return;
                            try {
                              const { data } = await api.post(`/fixed-assets/${scheduleOpen.id}/reverse-depreciation?year=${r.year}`);
                              toast.success(`Reversed ${r.year} · offset JE ${data.reversal_je_id.slice(-6)}`);
                              const { data: fresh } = await api.get(`/fixed-assets/${scheduleOpen.id}/schedule`);
                              setSchedule(fresh);
                              load();
                            } catch (err) { toast.error(err.response?.data?.detail || "Reversal failed"); }
                          }}>
                            <Undo2 className="w-3.5 h-3.5 mr-1 text-[#B4283A]" /> Reverse
                          </Button>
                        ) : (
                          <span className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Not posted</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ----------- Tax rates & summary -----------
function TaxTab() {
  const [rates, setRates] = useState([]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", rate: "", tax_type: "sales", is_default: false });

  const load = async () => {
    const [{ data: r }, { data: s }] = await Promise.all([
      api.get("/tax-rates"),
      api.get("/reports/tax-summary"),
    ]);
    setRates(r); setSummary(s);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    const payload = { name: form.name.trim(), rate: parseFloat(form.rate || 0), tax_type: form.tax_type, is_default: form.is_default };
    try {
      if (editing) await api.patch(`/tax-rates/${editing.id}`, payload);
      else await api.post("/tax-rates", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this tax rate?")) return;
    try { await api.delete(`/tax-rates/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-6">
      {summary && (
        <div className="grid grid-cols-3 gap-4">
          <div className="card-flat p-4"><div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Tax collected (invoices)</div><div className="font-mono-data text-lg mt-1 text-[#2F6B4A]" data-testid="tax-collected">{fm(summary.collected_total)}</div></div>
          <div className="card-flat p-4"><div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Tax paid (bills)</div><div className="font-mono-data text-lg mt-1 text-[#B4283A]" data-testid="tax-paid">{fm(summary.paid_total)}</div></div>
          <div className="card-flat p-4 border-l-4 border-l-[#E07A5F]"><div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Net payable</div><div className={`font-mono-data text-lg mt-1 ${summary.net_payable > 0 ? "text-[#B4283A]" : "text-[#2F6B4A]"}`} data-testid="tax-net">{fm(summary.net_payable)}</div></div>
        </div>
      )}
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="tax-new-btn" onClick={() => { setEditing(null); setForm({ name: "", rate: "", tax_type: "sales", is_default: false }); setOpen(true); }} className="rounded-full bg-[#E07A5F] hover:bg-[#c9694f] text-white h-10 px-4">
              <Plus className="w-4 h-4 mr-2" /> New tax rate
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>{editing ? "Edit tax rate" : "New tax rate"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Name</Label><Input data-testid="tax-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Standard VAT" /></div>
              <div><Label>Rate (%)</Label><Input type="number" step="0.01" data-testid="tax-form-rate" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} /></div>
              <div><Label>Type</Label>
                <Select value={form.tax_type} onValueChange={(v) => setForm({ ...form, tax_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sales">Sales Tax</SelectItem>
                    <SelectItem value="vat">VAT</SelectItem>
                    <SelectItem value="gst">GST</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="tax-form-save" onClick={save} className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="card-flat overflow-hidden">
        {rates.length === 0 ? (
          <div className="p-10 text-center text-sm text-[var(--muted-text)]">No tax rates yet. Add rates you'll apply to invoices and bills.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[var(--hover)] text-xs uppercase tracking-wider text-[var(--muted-text)]">
              <tr><th className="text-left px-5 py-2.5">Name</th><th className="text-left px-4 py-2.5">Type</th><th className="text-right px-4 py-2.5">Rate</th><th className="w-24" /></tr>
            </thead>
            <tbody>
              {rates.map((r) => (
                <tr key={r.id} data-testid={`tax-row-${r.id}`} className="border-b border-[var(--divider)] last:border-none">
                  <td className="px-5 py-3 font-medium">{r.name}</td>
                  <td className="px-4 py-3 text-[var(--muted-text)] capitalize">{r.tax_type}</td>
                  <td className="px-4 py-3 text-right font-mono">{r.rate}%</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="icon" variant="ghost" onClick={() => { setEditing(r); setForm({ ...r }); setOpen(true); }}><Pencil className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(r.id)}><Trash2 className="w-4 h-4 text-[#E63946]" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {summary && (summary.collected_by_rate.length > 0 || summary.paid_by_rate.length > 0) && (
        <div className="grid grid-cols-2 gap-4">
          <TaxBreakdown title="Collected · by rate" rows={summary.collected_by_rate} tone="income" />
          <TaxBreakdown title="Paid · by rate" rows={summary.paid_by_rate} tone="expense" />
        </div>
      )}
    </div>
  );
}

function TaxBreakdown({ title, rows, tone }) {
  return (
    <div className="card-flat overflow-hidden">
      <div className="px-5 py-2.5 border-b border-[var(--divider)] text-[10px] uppercase tracking-widest text-[var(--muted-text)]">{title}</div>
      {rows.length === 0 ? <div className="p-4 text-center text-xs text-[var(--muted-text)]">No activity</div> : (
        <table className="w-full text-sm">
          <thead><tr className="text-[10px] uppercase tracking-wider text-[var(--muted-text)]"><th className="text-left px-4 py-2">Rate</th><th className="text-right px-4 py-2">Taxable base</th><th className="text-right px-4 py-2">Tax</th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.rate} className="border-b border-[var(--divider)] last:border-none">
              <td className="px-4 py-2 font-mono">{r.rate}%</td>
              <td className="px-4 py-2 text-right font-mono">{fm(r.taxable_base)}</td>
              <td className={`px-4 py-2 text-right font-mono ${tone === "income" ? "text-[#2F6B4A]" : "text-[#B4283A]"}`}>{fm(r.tax)}</td>
            </tr>
          ))}</tbody>
        </table>
      )}
    </div>
  );
}

// ----------- Reconciliation -----------
function ReconciliationTab() {
  const location = useLocation();
  // Deep-link support: /financials?tab=reconciliation&account=<id> pre-selects
  // the account (used by the Bank page's per-account "Reconciled" badge).
  const initialAccountKey = (() => {
    const raw = new URLSearchParams(location.search).get("account");
    return raw ? `wallet:${raw}` : "";
  })();
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [history, setHistory] = useState([]);
  const [stmtBalance, setStmtBalance] = useState("");
  const [stmtEnd, setStmtEnd] = useState(format(new Date(), "yyyy-MM-dd"));
  const [completeOpen, setCompleteOpen] = useState(false);
  // Iter 34: QuickBooks-style statement import + auto-match
  const [csvText, setCsvText] = useState("");
  const [stmtLines, setStmtLines] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [matchAccepted, setMatchAccepted] = useState(new Set()); // statement_line_ids the user has ticked
  const [importOpen, setImportOpen] = useState(false);
  const [matching, setMatching] = useState(false);
  // Iter 72n: Statement Reconciliation Wizard — paste text / upload PDF / CSV,
  // tunable tolerance, one-click "Create missing" for lines not in the books,
  // downloadable PDF reconciliation report.
  const [importFormat, setImportFormat] = useState("csv"); // csv | text | pdf
  const [pasteText, setPasteText] = useState("");
  const [pdfName, setPdfName] = useState("");
  const [pdfBase64, setPdfBase64] = useState("");
  const [tolDateWindow, setTolDateWindow] = useState(7);
  const [tolAmount, setTolAmount] = useState(0);
  const [tolFuzzyDesc, setTolFuzzyDesc] = useState(true);
  const [unmatchedBook, setUnmatchedBook] = useState([]);
  const [missingSelected, setMissingSelected] = useState(new Set()); // statement_line_ids user wants to bulk-create
  const [creating, setCreating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // Iter 40: per-account reconciliation. `accountKey` is either "" (All accounts)
  // or "wallet:<id>" — the same format the backend expects on `account`.
  const [accounts, setAccounts] = useState([]);
  const [accountKey, setAccountKey] = useState(initialAccountKey);
  const { isActive, activeRail } = useCurrencyRail();
  // Iter 41: statement date range filter — narrows the uncleared list to the
  // period being reconciled. Empty = no bound.
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Currency used for THIS reconciliation session. When a specific account
  // is picked, it's that account's own currency. When "All accounts" is on,
  // fall back to the sidebar rail (USD or NGN), else default USD.
  const selectedAccount = accounts.find((a) => `wallet:${a.id}` === accountKey);
  const reconCcy =
    selectedAccount?.currency ||
    (activeRail === "USD" || activeRail === "NGN" ? activeRail : "USD");
  const fmR = (n) => fm(n, reconCcy);

  const accountParam = accountKey || undefined;

  const load = async () => {
    const params = {};
    if (accountParam) params.account = accountParam;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    const historyParams = accountParam ? { account: accountParam } : {};
    const [{ data: sess }, { data: h }] = await Promise.all([
      api.get("/reconciliation/session", { params }),
      api.get("/reconciliation/history", { params: historyParams }),
    ]);
    setData(sess); setHistory(h); setSelected(new Set());
    // Reset any in-flight statement matching when the account switches.
    setStmtLines([]); setSuggestions([]); setMatchAccepted(new Set()); setCsvText("");
    setPasteText(""); setPdfBase64(""); setPdfName(""); setUnmatchedBook([]);
    setMissingSelected(new Set());
  };
  useEffect(() => {
    (async () => {
      try {
        const { data: accs } = await api.get("/accounts");
        // Only real-money accounts are reconcilable.
        setAccounts((accs || []).filter(a => ["bank", "cash", "card", "wallet"].includes(a.type)));
      } catch (e) {
        // Non-blocking: reconciliation still works in "All accounts" mode when
        // the account list can't load. Log at debug so it's traceable.
        console.debug("reconciliation account list load failed:", e?.message);
      }
    })();
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [accountKey, dateFrom, dateTo]);

  const toggle = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };

  const markCleared = async (cleared) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    try {
      await api.post("/reconciliation/mark-cleared", { ids, cleared });
      toast.success(cleared ? "Marked cleared" : "Unmarked");
      load();
    } catch (err) { toast.error("Failed"); }
  };

  const completeRecon = async () => {
    if (!stmtBalance) return toast.error("Enter statement ending balance");
    try {
      const { data } = await api.post("/reconciliation/complete", {
        statement_end: stmtEnd,
        statement_ending_balance: parseFloat(stmtBalance),
        cleared_ids: [],
        account: accountParam,
      });
      toast.success(`Reconciled · difference ${fmR(data.difference)}`);
      setCompleteOpen(false);
      setStmtBalance("");
      load();
    } catch (err) { toast.error("Failed"); }
  };

  // Iter 34 handlers -----------------------------------------------------
  const parseStatement = async () => {
    // Build the payload based on the chosen input method.
    const payload = { format: importFormat };
    if (importFormat === "csv") {
      if (!csvText.trim()) return toast.error("Paste your statement CSV first");
      payload.csv = csvText;
    } else if (importFormat === "text") {
      if (!pasteText.trim()) return toast.error("Paste your statement text first");
      payload.text = pasteText;
    } else if (importFormat === "pdf") {
      if (!pdfBase64) return toast.error("Choose a PDF statement to upload");
      payload.pdf_base64 = pdfBase64;
    }
    try {
      const { data } = await api.post("/reconciliation/import-statement", payload);
      if (!data.count) return toast.error("No usable rows found — check headers or paste more detail");
      setStmtLines(data.statement_lines);
      // Immediately run auto-match on the same account scope, with user tolerance.
      setMatching(true);
      const { data: m } = await api.post("/reconciliation/auto-match", {
        statement_lines: data.statement_lines,
        account: accountParam,
        tolerance: {
          date_window_days: Number(tolDateWindow) || 7,
          amount_tolerance: Number(tolAmount) || 0,
          fuzzy_description: !!tolFuzzyDesc,
        },
      });
      setSuggestions(m.matches);
      setUnmatchedBook(m.unmatched_book || []);
      // Pre-tick the high-confidence ones (tx OR batch) so the user can bulk-confirm.
      setMatchAccepted(new Set(m.matches.filter(x => x.confidence >= 0.75 && (x.tx_id || x.batch_id)).map(x => x.statement_line_id)));
      // Pre-tick "missing" candidates (no book match at all) so the user can bulk-create.
      setMissingSelected(new Set(m.matches.filter(x => !(x.tx_id || x.batch_id)).map(x => x.statement_line_id)));
      toast.success(`Parsed ${data.count} lines · ${m.high_confidence} high-confidence matches`);
      setImportOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to parse statement");
    } finally { setMatching(false); }
  };

  const onPickPdf = (file) => {
    if (!file) { setPdfName(""); setPdfBase64(""); return; }
    if (!/pdf$/i.test(file.name) && file.type !== "application/pdf") {
      return toast.error("That doesn't look like a PDF file");
    }
    if (file.size > 5 * 1024 * 1024) return toast.error("PDF must be under 5 MB");
    const reader = new FileReader();
    reader.onload = () => {
      // FileReader gives us a data URL; strip the prefix — backend accepts either.
      const s = String(reader.result || "");
      setPdfBase64(s);
      setPdfName(file.name);
    };
    reader.onerror = () => toast.error("Could not read PDF");
    reader.readAsDataURL(file);
  };

  const toggleMissing = (slId) => {
    const n = new Set(missingSelected);
    if (n.has(slId)) n.delete(slId); else n.add(slId);
    setMissingSelected(n);
  };

  const createMissing = async () => {
    if (!accountParam || !accountParam.startsWith("wallet:")) {
      return toast.error("Pick a specific account first — create-missing needs a currency rail");
    }
    const ids = Array.from(missingSelected);
    const chosen = stmtLines.filter(l => ids.includes(l.id));
    if (!chosen.length) return toast.error("Nothing selected to create");
    setCreating(true);
    try {
      const { data: r } = await api.post("/reconciliation/create-missing", {
        account: accountParam,
        lines: chosen.map(l => ({ id: l.id, date: l.date, amount: l.amount, memo: l.memo })),
        mark_cleared: true,
      });
      toast.success(`${r.created} transaction${r.created === 1 ? "" : "s"} created & cleared`);
      // Drop them from unmatched suggestions
      setSuggestions((prev) => prev.filter(s => !ids.includes(s.statement_line_id)));
      setStmtLines((prev) => prev.filter(l => !ids.includes(l.id)));
      setMissingSelected(new Set());
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create missing transactions");
    } finally { setCreating(false); }
  };

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const matched = suggestions
        .filter(s => s.tx_id || s.batch_id)
        .map(s => {
          const line = stmtLines.find(l => l.id === s.statement_line_id) || {};
          return { date: line.date, amount: line.amount, memo: line.memo || s.tx_note || "" };
        });
      const unmatchedStatement = suggestions
        .filter(s => !(s.tx_id || s.batch_id))
        .map(s => {
          const line = stmtLines.find(l => l.id === s.statement_line_id) || {};
          return { date: line.date, amount: line.amount, memo: line.memo };
        });
      const duplicates = stmtLines.filter(l => l.is_duplicate)
        .map(l => ({ date: l.date, amount: l.amount, memo: l.memo }));
      const resp = await api.post("/reconciliation/report-pdf", {
        account: accountParam,
        statement_end: stmtEnd,
        statement_ending_balance: parseFloat(stmtBalance || "0"),
        book_balance: data?.cleared_balance || 0,
        matched,
        unmatched_statement: unmatchedStatement,
        unmatched_book: unmatchedBook.map(t => ({
          date: t.date, amount: (t.type === "expense" ? -1 : 1) * t.amount, note: t.note,
        })),
        duplicates,
      }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `reconciliation-${stmtEnd}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Reconciliation report downloaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to build PDF report");
    } finally { setDownloading(false); }
  };

  const confirmMatches = async () => {
    const pairs = suggestions
      .filter(s => matchAccepted.has(s.statement_line_id) && (s.tx_id || s.batch_id))
      .map(s => {
        const line = stmtLines.find(l => l.id === s.statement_line_id);
        return {
          // Prefer batch_id when the candidate is a payment_batch (iter 67);
          // fall back to tx_id for regular book transactions.
          ...(s.batch_id ? { batch_id: s.batch_id } : { tx_id: s.tx_id }),
          statement_line_id: s.statement_line_id,
          date: line?.date, amount: line?.amount, memo: line?.memo,
        };
      });
    if (!pairs.length) return toast.error("No matches ticked");
    try {
      const { data: r } = await api.post("/reconciliation/confirm-matches", { pairs });
      const msgs = [];
      if (r.cleared) msgs.push(`${r.cleared} transaction${r.cleared === 1 ? "" : "s"} cleared`);
      if (r.batches_linked) msgs.push(`${r.batches_linked} deposit${r.batches_linked === 1 ? "" : "s"} linked`);
      toast.success(msgs.join(" · ") || "Matches confirmed");
      setStmtLines([]); setSuggestions([]); setMatchAccepted(new Set()); setCsvText("");
      load();
    } catch (err) { toast.error("Failed to confirm matches"); }
  };

  const toggleMatch = (slId) => {
    const n = new Set(matchAccepted);
    n.has(slId) ? n.delete(slId) : n.add(slId);
    setMatchAccepted(n);
  };

  const selectedSum = data ? Array.from(selected).reduce((acc, id) => {
    const tx = data.unreconciled.find((t) => t.id === id);
    if (!tx) return acc;
    return acc + (tx.type === "expense" ? -tx.amount : tx.type === "income" ? tx.amount : 0);
  }, 0) : 0;

  // Running difference to the statement's ending balance — updates as user
  // ticks items. Difference == 0 means the account is fully reconciled.
  const stmtBalNum = parseFloat(stmtBalance || "0");
  const projectedCleared = (data?.cleared_balance || 0) + selectedSum;
  const difference = stmtBalance === "" ? null : Number((projectedCleared - stmtBalNum).toFixed(2));
  const isBalanced = difference !== null && Math.abs(difference) < 0.01;

  if (!data) return <div className="text-sm text-[var(--muted-text)]">Loading…</div>;

  return (
    <div className="space-y-4">
      {/* Bank account selector — reconcile each account against its own statement */}
      <div className="card-flat p-4 flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Reconciling account</div>
          <div className="text-sm text-[var(--muted-text)] mt-0.5">
            {accountKey ? "Only transactions for this account are shown below." : "All bank & wallet accounts (choose one to reconcile against a specific statement)."}
          </div>
        </div>
        <div className="w-full sm:w-64">
          <Select value={accountKey || "__all__"} onValueChange={(v) => setAccountKey(v === "__all__" ? "" : v)}>
            <SelectTrigger data-testid="recon-account-select" className="h-9">
              <SelectValue placeholder="All accounts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__" data-testid="recon-account-all">All accounts</SelectItem>
              {accounts.filter((a) => isActive(a.currency || "USD")).map((a) => (
                <SelectItem key={a.id} value={`wallet:${a.id}`} data-testid={`recon-account-${a.id}`}>
                  {a.name}
                  {a.institution_name ? ` · ${a.institution_name}` : ""}
                  {a.type ? ` (${a.type})` : ""}
                  <span className="ml-1 text-[10px] text-[var(--muted-text)]">· {a.currency || "USD"}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {/* Statement date-range filter */}
        <div className="grid grid-cols-2 gap-2 w-full sm:w-72">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Period from</div>
            <Input data-testid="recon-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1 h-9 text-sm" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Period to</div>
            <Input data-testid="recon-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1 h-9 text-sm" />
          </div>
        </div>
        {(dateFrom || dateTo) && (
          <Button variant="ghost" size="sm" className="h-9 text-[var(--muted-text)]"
                  data-testid="recon-date-clear" onClick={() => { setDateFrom(""); setDateTo(""); }}>
            Clear dates
          </Button>
        )}
      </div>

      {/* Statement-anchored summary */}
      <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]" data-testid="recon-currency-label">
        <span>Reconciling in</span>
        <span className="px-1.5 py-0.5 rounded font-mono text-[10px]" style={{ backgroundColor: reconCcy === "USD" ? "#1E5A96" : "#4C956C", color: "#FDFCF8" }}>{reconCcy}</span>
        {!selectedAccount && activeRail === "ALL" && (
          <span className="normal-case tracking-normal text-[10px]">· pick an account or set the sidebar rail to reconcile a specific currency</span>
        )}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="card-flat p-4">
          <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Statement date</div>
          <Input type="date" data-testid="recon-stmt-date" value={stmtEnd} onChange={(e) => setStmtEnd(e.target.value)} className="mt-1 h-8 text-sm" />
        </div>
        <div className="card-flat p-4">
          <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Statement ending balance</div>
          <Input type="number" step="0.01" placeholder="0.00" data-testid="recon-stmt-balance-top" value={stmtBalance} onChange={(e) => setStmtBalance(e.target.value)} className="mt-1 h-8 text-sm font-mono" />
        </div>
        <div className="card-flat p-4">
          <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Cleared book balance</div>
          <div className="font-mono-data text-lg mt-1" data-testid="recon-cleared-balance">{fmR(data.cleared_balance)}</div>
        </div>
        <div className="card-flat p-4">
          <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Projected cleared</div>
          <div className="font-mono-data text-lg mt-1" data-testid="recon-projected">{fmR(projectedCleared)}</div>
          <div className="text-[10px] text-[var(--muted-text)]">+ {selected.size} selected</div>
        </div>
        <div className={`card-flat p-4 border-l-4 ${
          difference === null ? "border-l-[var(--border)]" :
          isBalanced ? "border-l-[#2F6B4A]" : "border-l-[#B4283A]"
        }`}>
          <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Difference to statement</div>
          <div className={`font-mono-data text-lg mt-1 ${isBalanced ? "text-[#2F6B4A]" : difference !== null ? "text-[#B4283A]" : ""}`} data-testid="recon-difference">
            {difference === null ? "—" : fmR(difference)}
          </div>
          <div className="text-[10px] text-[var(--muted-text)]">{isBalanced ? "Balanced — ready to finish" : difference === null ? "Enter statement balance" : "Tick items to close the gap"}</div>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <Button variant="outline" size="sm" onClick={() => markCleared(true)} disabled={!selected.size} data-testid="recon-clear-btn"><CheckCircle2 className="w-4 h-4 mr-2" /> Mark cleared</Button>
        <Button variant="outline" size="sm" onClick={() => markCleared(false)} disabled={!selected.size}>Unmark</Button>
        <Dialog open={importOpen} onOpenChange={setImportOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" data-testid="recon-import-btn">
              <BookOpenCheck className="w-4 h-4 mr-2" /> Import statement
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl" data-testid="recon-import-dialog">
            <DialogHeader>
              <DialogTitle>Statement Reconciliation Wizard</DialogTitle>
              <DialogDescription>
                Bring in your bank statement three ways. We'll auto-match against your books, flag what's missing on either side, and let you download a PDF reconciliation report.
              </DialogDescription>
            </DialogHeader>
            <Tabs value={importFormat} onValueChange={setImportFormat} className="mt-1">
              <TabsList className="grid grid-cols-3 w-full" data-testid="recon-import-tabs">
                <TabsTrigger value="csv" data-testid="recon-import-tab-csv"><Copy className="w-3.5 h-3.5 mr-1.5" /> CSV</TabsTrigger>
                <TabsTrigger value="text" data-testid="recon-import-tab-text"><FileText className="w-3.5 h-3.5 mr-1.5" /> Paste text</TabsTrigger>
                <TabsTrigger value="pdf" data-testid="recon-import-tab-pdf"><Download className="w-3.5 h-3.5 mr-1.5 rotate-180" /> Upload PDF</TabsTrigger>
              </TabsList>
              <TabsContent value="csv" className="mt-3">
                <div className="text-xs text-[var(--muted-text)] mb-1.5">
                  Columns supported: <span className="font-mono">Date, Amount, Description</span> — or <span className="font-mono">Date, Debit, Credit, Description</span>.
                </div>
                <textarea
                  data-testid="recon-csv-input"
                  className="w-full h-56 border border-[var(--border)] rounded-md p-3 font-mono text-xs"
                  placeholder={"Date,Amount,Description\n2026-02-15,-45.00,UBER TRIP\n2026-02-16,1200.00,ACH TRANSFER INV-2026-0001"}
                  value={csvText}
                  onChange={(e) => setCsvText(e.target.value)}
                />
              </TabsContent>
              <TabsContent value="text" className="mt-3">
                <div className="text-xs text-[var(--muted-text)] mb-1.5">
                  Paste any raw text from your bank — each line needs a date and an amount. We'll figure out the rest.
                </div>
                <textarea
                  data-testid="recon-text-input"
                  className="w-full h-56 border border-[var(--border)] rounded-md p-3 font-mono text-xs"
                  placeholder={"02/15/2026  UBER TRIP           -45.00\nFeb 16, 2026  ACH TRANSFER INV-2026-0001  1,200.00"}
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                />
              </TabsContent>
              <TabsContent value="pdf" className="mt-3">
                <div className="text-xs text-[var(--muted-text)] mb-2">
                  Upload a text-based PDF statement (max 5 MB). Scanned image PDFs won't work — paste the text or use CSV instead.
                </div>
                <div className="border-2 border-dashed border-[var(--border)] rounded-md p-5 text-center hover:bg-[var(--row-hover)]">
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    id="recon-pdf-file"
                    data-testid="recon-pdf-input"
                    className="hidden"
                    onChange={(e) => onPickPdf(e.target.files?.[0])}
                  />
                  <label htmlFor="recon-pdf-file" className="cursor-pointer inline-flex items-center gap-2 text-sm">
                    <FileText className="w-5 h-5 text-[var(--muted-text)]" />
                    {pdfName ? <span className="font-medium">{pdfName}</span> : <span>Click to choose a PDF file</span>}
                  </label>
                  {pdfBase64 && (
                    <div className="mt-2 text-[10px] text-[#2F6B4A] uppercase tracking-widest">Ready to parse</div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
            {/* Tolerance controls apply to all three input methods */}
            <div className="mt-3 p-3 rounded-md bg-[var(--track)]/40 border border-[var(--divider)]">
              <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] mb-2 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> Match tolerance
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-[10px]">Date window (± days)</Label>
                  <Input
                    type="number" min="0" max="30" step="1"
                    data-testid="recon-tol-date"
                    value={tolDateWindow}
                    onChange={(e) => setTolDateWindow(e.target.value)}
                    className="h-8 mt-1 text-sm font-mono"
                  />
                </div>
                <div>
                  <Label className="text-[10px]">Amount tolerance ({reconCcy})</Label>
                  <Input
                    type="number" min="0" step="0.01"
                    data-testid="recon-tol-amount"
                    value={tolAmount}
                    onChange={(e) => setTolAmount(e.target.value)}
                    className="h-8 mt-1 text-sm font-mono"
                  />
                </div>
                <div className="flex items-end pb-1">
                  <label className="inline-flex items-center gap-2 text-xs cursor-pointer" data-testid="recon-tol-fuzzy">
                    <input
                      type="checkbox"
                      checked={tolFuzzyDesc}
                      onChange={(e) => setTolFuzzyDesc(e.target.checked)}
                      className="rounded"
                    />
                    Fuzzy description
                  </label>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setImportOpen(false)}>Cancel</Button>
              <Button data-testid="recon-parse-btn" onClick={parseStatement} disabled={matching}>
                {matching ? "Matching…" : "Parse & auto-match"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        <div className="flex-1" />
        <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
          <DialogTrigger asChild>
            <Button className={`rounded-full text-white ${isBalanced ? "bg-[#2F6B4A] hover:bg-[#245536]" : "bg-[#2F4F4F] hover:bg-[#213838]"}`} data-testid="recon-complete-btn">
              {isBalanced ? "Finish reconciliation" : "Complete reconciliation"}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Complete reconciliation</DialogTitle><DialogDescription>Snapshot your bank statement's ending balance and lock the reconciliation. The original journal entries stay intact.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div><Label>Statement end date</Label><Input type="date" value={stmtEnd} onChange={(e) => setStmtEnd(e.target.value)} /></div>
              <div><Label>Statement ending balance</Label><Input type="number" step="0.01" data-testid="recon-stmt-balance" value={stmtBalance} onChange={(e) => setStmtBalance(e.target.value)} /></div>
              <div className="text-xs text-[var(--muted-text)]">Current cleared book balance: <span className="font-mono">{fmR(data.cleared_balance)}</span></div>
              {difference !== null && (
                <div className={`text-xs font-medium ${isBalanced ? "text-[#2F6B4A]" : "text-[#B4283A]"}`}>
                  Difference at close: <span className="font-mono">{fmR(difference)}</span>{isBalanced ? " · balanced ✓" : ""}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setCompleteOpen(false)}>Cancel</Button>
              <Button onClick={completeRecon} data-testid="recon-complete-save" className="rounded-full bg-[#2F6B4A] hover:bg-[#245536] text-white">Complete</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Auto-match suggestions panel (visible after CSV import) */}
      {suggestions.length > 0 && (
        <div className="card-flat overflow-hidden" data-testid="recon-suggestions-panel">
          <div className="px-5 py-2.5 border-b border-[var(--divider)] flex items-center justify-between flex-wrap gap-2">
            <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">
              Statement matches · {stmtLines.length} lines · {suggestions.filter(s => s.confidence >= 0.75 && (s.tx_id || s.batch_id)).length} high-confidence
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button size="sm" variant="ghost" onClick={() => { setStmtLines([]); setSuggestions([]); setMatchAccepted(new Set()); setCsvText(""); setUnmatchedBook([]); setMissingSelected(new Set()); }} data-testid="recon-clear-suggestions">Clear</Button>
              <Button size="sm" variant="outline" onClick={downloadReport} disabled={downloading} data-testid="recon-download-report">
                <Download className="w-3.5 h-3.5 mr-1.5" /> {downloading ? "Building…" : "Download PDF report"}
              </Button>
              {missingSelected.size > 0 && (
                <Button size="sm" variant="outline" onClick={createMissing} disabled={creating || !accountParam} data-testid="recon-create-missing"
                        className="border-[#B4671A] text-[#B4671A] hover:bg-[#FFF3E0]">
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> {creating ? "Creating…" : `Create ${missingSelected.size} missing`}
                </Button>
              )}
              <Button size="sm" data-testid="recon-confirm-matches" onClick={confirmMatches} disabled={matchAccepted.size === 0} className="bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white">
                Confirm {matchAccepted.size} match{matchAccepted.size === 1 ? "" : "es"}
              </Button>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[var(--muted-text)] border-b border-[var(--divider)]">
                <th className="text-left py-2 pl-5 w-10"></th>
                <th className="text-left py-2">Statement line</th>
                <th className="text-right py-2 font-mono">Amount</th>
                <th className="text-left py-2 pl-6">→ Matched transaction</th>
                <th className="text-center py-2 w-24">Confidence</th>
              </tr>
            </thead>
            <tbody>
              {suggestions.map((s) => {
                const line = stmtLines.find(l => l.id === s.statement_line_id);
                const ticked = matchAccepted.has(s.statement_line_id);
                const hasCandidate = !!(s.tx_id || s.batch_id);
                const isBatch = !!s.batch_id;
                const missingTicked = missingSelected.has(s.statement_line_id);
                const isDup = !!line?.is_duplicate;
                const rowClick = () => {
                  if (hasCandidate) toggleMatch(s.statement_line_id);
                  else toggleMissing(s.statement_line_id);
                };
                return (
                  <tr key={s.statement_line_id} data-testid={`recon-match-${s.statement_line_id}`}
                      className={`border-b border-[var(--divider)] last:border-none cursor-pointer hover:bg-[var(--row-hover)] ${
                        hasCandidate && ticked ? "bg-[var(--brand-light)]" : ""
                      } ${!hasCandidate && missingTicked ? "bg-[#FFF3E0]" : ""}`}
                      onClick={rowClick}>
                    <td className="pl-5 py-3 w-10">
                      {hasCandidate
                        ? (ticked ? <CheckSquare className="w-4 h-4 text-[var(--brand)]" /> : <Square className="w-4 h-4 text-[var(--muted-text)]" />)
                        : (missingTicked ? <CheckSquare className="w-4 h-4 text-[#B4671A]" /> : <Square className="w-4 h-4 text-[var(--muted-text)]" />)}
                    </td>
                    <td className="py-3">
                      <div className="text-xs text-[var(--muted-text)] font-mono flex items-center gap-1.5">
                        {line?.date}
                        {isDup && (
                          <span data-testid={`recon-dup-badge-${s.statement_line_id}`} className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] uppercase tracking-wider bg-[#FFEBEE] text-[#B4283A]">
                            <AlertTriangle className="w-2.5 h-2.5 mr-1" /> Duplicate
                          </span>
                        )}
                      </div>
                      <div className="truncate max-w-[220px]">{line?.memo || "—"}</div>
                    </td>
                    <td className="py-3 text-right font-mono">{fmR(line?.amount)}</td>
                    <td className="py-3 pl-6">
                      {hasCandidate ? (
                        <>
                          <div className="text-xs text-[var(--muted-text)] font-mono flex items-center gap-2">
                            {s.tx_date} · {s.tx_type}
                            {isBatch && (
                              <span data-testid={`recon-match-badge-batch-${s.statement_line_id}`} className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] uppercase tracking-wider bg-[#EDF3EF] text-[#4C956C]">
                                Reallocated deposit
                              </span>
                            )}
                          </div>
                          <div className="truncate max-w-[240px]">{s.tx_note || "—"}</div>
                          <div className="text-[10px] text-[var(--muted-text)]">{s.reason}</div>
                        </>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-[#B4283A]">{s.reason}</span>
                          <span className="text-[10px] text-[var(--muted-text)]">· tick to create as a new transaction</span>
                        </div>
                      )}
                    </td>
                    <td className="py-3 text-center pr-5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-medium ${
                        s.level === "high" ? "bg-[#E8F5EC] text-[#2F6B4A]" :
                        s.level === "medium" ? "bg-[#FFF3E0] text-[#B4671A]" :
                        hasCandidate ? "bg-[#FFEBEE] text-[#B4283A]" : "bg-[var(--track)] text-[var(--muted-text)]"
                      }`}>
                        {hasCandidate ? `${Math.round(s.confidence * 100)}%` : "missing"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {/* Unmatched on the BOOK side — book txs the statement doesn't show. */}
      {unmatchedBook.length > 0 && (
        <div className="card-flat overflow-hidden" data-testid="recon-unmatched-book">
          <div className="px-5 py-2.5 border-b border-[var(--divider)] flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5 text-[#B4671A]" />
            <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">
              In your books but not on this statement · {unmatchedBook.length}
            </div>
          </div>
          <table className="w-full text-sm">
            <tbody>
              {unmatchedBook.slice(0, 100).map((t) => (
                <tr key={t.tx_id} className="border-b border-[var(--divider)] last:border-none">
                  <td className="pl-5 py-2.5 w-28 text-[var(--muted-text)] font-mono text-xs">{t.date}</td>
                  <td className="py-2.5 flex-1">
                    <span className="capitalize text-[var(--muted-text)] text-xs mr-2">{t.type}</span>
                    {t.category}
                    <span className="text-[var(--muted-text)] ml-2">— {t.note}</span>
                  </td>
                  <td className="py-2.5 pr-5 text-right font-mono">
                    {t.type === "expense" ? "−" : "+"}{fm(t.amount, t.currency || reconCcy)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {unmatchedBook.length > 100 && (
            <div className="px-5 py-2 text-[10px] text-[var(--muted-text)] italic">… and {unmatchedBook.length - 100} more</div>
          )}
        </div>
      )}

      <div className="card-flat overflow-hidden">
        <div className="px-5 py-2.5 border-b border-[var(--divider)] text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Uncleared transactions · {data.unreconciled.length}</div>
        {data.unreconciled.length === 0 ? (
          <div className="p-10 text-center text-sm text-[var(--muted-text)]">Everything cleared. 🎉</div>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {data.unreconciled.filter((t) => activeRail === "ALL" || (t.currency || "USD") === reconCcy).map((t) => {
                const isSel = selected.has(t.id);
                const txCcy = t.currency || reconCcy;
                return (
                  <tr key={t.id} data-testid={`recon-tx-${t.id}`} className={`border-b border-[var(--divider)] last:border-none ${isSel ? "bg-[#F5EAE3]" : ""} cursor-pointer hover:bg-[var(--row-hover)]`} onClick={() => toggle(t.id)}>
                    <td className="pl-5 py-3 w-10">{isSel ? <CheckSquare className="w-4 h-4 text-[#2F4F4F]" /> : <Square className="w-4 h-4 text-[var(--muted-text)]" />}</td>
                    <td className="py-3 w-28 text-[var(--muted-text)] font-mono text-xs">{t.date}</td>
                    <td className="py-3 flex-1"><span className="capitalize text-[var(--muted-text)] text-xs mr-2">{t.type}</span>{t.category}<span className="text-[var(--muted-text)] ml-2">— {t.note}</span></td>
                    <td className="py-3 pr-5 text-right font-mono">{t.type === "expense" ? "−" : "+"}{fm(t.amount, txCcy)}</td>
                    <td className="pr-5 py-3 text-[10px] uppercase text-[var(--muted-text)]">{t.source}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {history.length > 0 && (
        <div className="card-flat overflow-hidden">
          <div className="px-5 py-2.5 border-b border-[var(--divider)] text-[10px] uppercase tracking-widest text-[var(--muted-text)]">
            Reconciliation history{accountKey ? " · this account" : ""}
          </div>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-[var(--muted-text)]"><tr><th className="text-left px-5 py-2">Statement date</th><th className="text-left px-4 py-2">Account</th><th className="text-right px-4 py-2">Statement bal</th><th className="text-right px-4 py-2">Book bal</th><th className="text-right px-4 py-2">Difference</th></tr></thead>
            <tbody>
              {history.map((h) => {
                const accId = (h.account || "").startsWith("wallet:") ? h.account.split(":", 2)[1] : null;
                const acc = accId ? accounts.find(a => a.id === accId) : null;
                return (
                  <tr key={h.id} className="border-b border-[var(--divider)] last:border-none">
                    <td className="px-5 py-2.5">{h.statement_end}</td>
                    <td className="px-4 py-2.5 text-[var(--muted-text)]">{acc ? acc.name : (h.account || "All accounts")}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{fmR(h.statement_ending_balance)}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{fmR(h.book_balance)}</td>
                    <td className={`px-4 py-2.5 text-right font-mono ${Math.abs(h.difference) < 0.005 ? "text-[#2F6B4A]" : "text-[#B4283A]"}`}>{fmR(h.difference)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ----------- 13-week Cash Forecast -----------
function ForecastTab() {
  const [data, setData] = useState(null);
  const [weeks, setWeeks] = useState(13);
  const load = useCallback(async () => {
    const { data } = await api.get("/reports/cash-forecast", { params: { weeks } });
    setData(data);
  }, [weeks]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="text-sm text-[var(--muted-text)]">Loading…</div>;

  const chartData = [
    { week: "W0", cash: data.starting_cash },
    ...data.buckets.map((b) => ({ week: `W${b.week}`, cash: b.ending_cash, inflow: b.inflow, outflow: b.outflow })),
  ];
  const minCash = Math.min(...data.buckets.map((b) => b.ending_cash));
  const negativeWeek = data.buckets.find((b) => b.ending_cash < 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-4">
        <div className="card-flat p-4"><div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Starting cash</div><div className="font-mono-data text-lg mt-1" data-testid="fc-starting">{fm(data.starting_cash)}</div></div>
        <div className="card-flat p-4"><div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Final cash · W{data.weeks}</div><div className="font-mono-data text-lg mt-1" data-testid="fc-final">{fm(data.final_cash)}</div></div>
        <div className="card-flat p-4"><div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Lowest projected</div><div className={`font-mono-data text-lg mt-1 ${minCash < 0 ? "text-[#B4283A]" : ""}`} data-testid="fc-lowest">{fm(minCash)}</div></div>
        <div className="card-flat p-4"><div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Horizon</div>
          <Select value={String(weeks)} onValueChange={(v) => setWeeks(parseInt(v))}>
            <SelectTrigger className="h-9 mt-1" data-testid="fc-horizon"><SelectValue /></SelectTrigger>
            <SelectContent>{[4, 8, 13, 26, 52].map((w) => <SelectItem key={w} value={String(w)}>{w} weeks</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      {negativeWeek && (
        <div className="card-flat p-4 border-l-4 border-l-[#B4283A] bg-[#FCE7E9]/40" data-testid="fc-alert-negative">
          <div className="text-sm font-medium text-[#B4283A]">Cash goes negative in week {negativeWeek.week} ({negativeWeek.start})</div>
          <div className="text-xs text-[var(--muted-text)] mt-1">Chase overdue AR, delay non-essential payables, or draw on credit before that week.</div>
        </div>
      )}
      <div className="card-flat p-6">
        <div className="text-xs uppercase tracking-[0.2em] text-[var(--muted-text)] mb-4">Projected cash balance</div>
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="2 2" stroke="var(--divider)" />
              <XAxis dataKey="week" style={{ fontSize: 11 }} />
              <YAxis style={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => fm(v)} />
              <ReferenceLine y={0} stroke="#B4283A" strokeDasharray="3 3" />
              <Line type="monotone" dataKey="cash" stroke="#2F4F4F" strokeWidth={2.5} dot={{ r: 3, fill: "#E07A5F" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="card-flat overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--hover)] text-xs uppercase tracking-wider text-[var(--muted-text)]">
            <tr><th className="text-left px-5 py-2.5">Week</th><th className="text-left px-4 py-2.5">Range</th><th className="text-right px-4 py-2.5">Inflow</th><th className="text-right px-4 py-2.5">Outflow</th><th className="text-right px-4 py-2.5">Net</th><th className="text-right px-4 py-2.5">Ending cash</th></tr>
          </thead>
          <tbody>
            {data.buckets.map((b) => (
              <tr key={b.week} data-testid={`fc-week-${b.week}`} className="border-b border-[var(--divider)] last:border-none">
                <td className="px-5 py-2.5 font-mono text-xs">W{b.week}</td>
                <td className="px-4 py-2.5 text-[var(--muted-text)] text-xs">{b.start} → {b.end}</td>
                <td className="px-4 py-2.5 text-right font-mono text-[#2F6B4A]">{fm(b.inflow)}</td>
                <td className="px-4 py-2.5 text-right font-mono text-[#B4283A]">{fm(b.outflow)}</td>
                <td className="px-4 py-2.5 text-right font-mono">{fm(b.net)}</td>
                <td className={`px-4 py-2.5 text-right font-mono ${b.ending_cash < 0 ? "text-[#B4283A]" : ""}`}>{fm(b.ending_cash)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
